export const colorFields = [
    { label: 'Main background color', id: 'main_bg_color' },
    { label: 'Chat button background', id: 'open_btn_bg' },
    { label: 'Button text color', id: 'btn_text_color' },
    { label: 'Message icon color', id: 'msg_icon_color' },
    { label: 'Shadow color', id: 'single_shadow' },
    { label: 'Selected option highlight', id: 'options_select_highlight' },
    { label: 'Hover text color', id: 'hover_text_color' },
    { label: 'Default white color', id: 'white_color_used' },
];

export const cssKeys = [
    'main-bg-color',
    'openButtonBground',
    'buttonTextColor',
    'messageIcon',
    'single-shadow',
    'Options-select-hilight',
    'hoverTextColor',
    'whiteColorUsed',
    'font-family'
];

export const TREATMENT_SUB_MENU = [
    { value: 'treatmentOffers', label: 'Special offers' },
    { value: 'treatmentMessages', label: 'Treatment messages' },
    { value: 'treatmentUrls', label: 'Related links' },
];

export const configKeys = [
    'virtual_consultation',
    'agentId',
    'bot_name',
    'projectId',
    'disabledPages',
    'maxSessionTime',
    'actionButtonTitle',
    'botpressbotId',
    'whatsappUrl',
    'practicePhoneNumber',
    'adwords_conversion_id',
    'adword_conversion_labels',
    'autoOpen',
    'messagePacing',
    'messageDelayMs',
    // Where the widget is actually embedded. Decoupled from Practice.contact.website
    // (the default practice site). Stored in the platform settings.config JSON, so no
    // migration is required. Surfaced in the super admin "Where it runs" column + drawer.
    'hostUrls',
    // Tappable quick-reply options the bot offers (array of label strings). Config-backed
    // "building block" — additive, no migration. First of the extensible blocks
    // (video/calendar/Dentally to follow the same config-key pattern).
    'quickReplies',
    // Configurable quick-select menu — array of action objects
    // { id, label, icon, order, is_enabled, handler_type, config }. Drives the chatbot's
    // top-level menu and the CRM action dispatcher. Config-backed (no migration) for phase 1.
    'actions',
];

// Friendly labels for known Botpress bot IDs, so the super admin console can resolve a
// raw bot id to the flow it runs. Unknown ids fall back to "Custom flow".
export const BOTPRESS_FLOW_LABELS = {
    bot_071024: 'Standard flow',
    base_bot_new_flow: 'New flow — privacy + existing-patient gating',
    bot001: 'Legacy flow',
};

export const resolveFlowLabel = (botId) => {
    const id = (botId || '').trim();
    if (!id) return '';
    return BOTPRESS_FLOW_LABELS[id] || 'Custom flow';
};

// ============================================================================
// Quick-action INTENT model (P3 revamp)
// ----------------------------------------------------------------------------
// The admin-facing vocabulary is a small set of INTENTS. Each intent maps to the
// handler_type + config the CRM dispatcher already understands, so the stored
// `actions[]` shape (settings.config.actions) and the /execute contract are
// unchanged. Integration-dependent behaviour (which integration, fallbacks) is
// derived server-side, so those internals are hidden from the admin.
//
//   enquiry    -> handler_type: 'crm_form'  (funnel front-half: capture -> lead)
//   book       -> handler_type: 'dentally', config.operation='book'
//   emergency  -> handler_type: 'dentally', config.operation='book', reason='emergency'
//   reschedule -> handler_type: 'dentally', config.operation='reschedule'
//   cancel     -> handler_type: 'dentally', config.operation='cancel'
//   link       -> handler_type: 'link',     config.url (opens a web page in a new tab)
//
// The dispatcher degrades gracefully when Dentally/Stripe aren't connected
// (manual capture + task + optional booking URL), so every intent is always
// selectable — no hard gating in the UI.
// ============================================================================
export const ACTION_INTENTS = [
    {
        value: 'enquiry',
        label: 'Enquire about a treatment',
        icon: 'solar:chat-square-like-bold-duotone',
        hint: 'Capture the visitor’s details and create a lead (the enquiry funnel).',
        handler_type: 'crm_form',
        // Meaningful per-intent fields the admin may edit. 'confirmation_template' is
        // the engagement-template selector, only shown when the channel is a real one.
        fields: ['label', 'confirmation', 'confirmation_template'],
    },
    {
        value: 'book',
        label: 'Book appointment',
        icon: 'solar:calendar-add-bold-duotone',
        hint: 'Books via Dentally when connected; otherwise captures the enquiry and creates a follow-up task.',
        handler_type: 'dentally',
        operation: 'book',
        fields: ['label', 'appointment_reason', 'duration', 'deposit', 'quick_contact', 'confirmation', 'confirmation_template'],
    },
    {
        value: 'emergency',
        label: 'Book emergency appointment',
        icon: 'solar:danger-triangle-bold-duotone',
        hint: 'Always surfaces the practice phone / WhatsApp. Books via Dentally (when accepting emergencies) or raises a HIGH-priority task.',
        handler_type: 'dentally',
        operation: 'book',
        reason: 'emergency',
        fields: ['label', 'appointment_reason', 'duration', 'confirmation', 'confirmation_template'],
    },
    {
        value: 'reschedule',
        label: 'Reschedule appointment',
        icon: 'solar:calendar-search-bold-duotone',
        hint: 'Finds the patient’s upcoming appointments (Dentally) to move — otherwise raises a task.',
        handler_type: 'dentally',
        operation: 'reschedule',
        fields: ['label', 'confirmation', 'confirmation_template'],
    },
    {
        value: 'cancel',
        label: 'Cancel appointment',
        icon: 'solar:calendar-mark-bold-duotone',
        hint: 'Finds the patient’s upcoming appointments (Dentally) to cancel — otherwise raises a task.',
        handler_type: 'dentally',
        operation: 'cancel',
        fields: ['label', 'confirmation', 'confirmation_template'],
    },
    {
        value: 'link',
        label: 'Open a link',
        icon: 'solar:link-round-bold-duotone',
        hint: 'Opens a web page (for example your online booking page) in a new tab. The chat stays closed.',
        handler_type: 'link',
        fields: ['label', 'url'],
    },
];

// Which meaningful sub-fields each intent exposes (drives the per-action editor).
export const intentByValue = Object.fromEntries(ACTION_INTENTS.map((i) => [i.value, i]));

// Confirmation channels for an action's outcome. 'auto' = use the lead's
// preferred method, else in-chat.
export const CONFIRM_CHANNELS = [
    { value: 'in_chat', label: 'In chat only' },
    { value: 'auto', label: 'Auto (use preferred, else in chat)' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
];

// Which confirmation channels are "real" delivery channels (vs in-chat/auto) — a
// template can only be picked for these. Maps the UI's lowercase channel value to
// the backend TEMPLATE_CHANNEL_CHOICES value used for `channel__in` filtering.
export const TEMPLATE_CHANNEL_BY_CONFIRM = {
    email: 'EMAIL',
    sms: 'SMS',
    whatsapp: 'WHATSAPP',
};
// A confirmation channel is "real" (needs a template) only when it maps to a channel.
export const isRealConfirmChannel = (ch) => !!TEMPLATE_CHANNEL_BY_CONFIRM[ch];

// Deposit timing (Dentally-only per design D1 — deposits require a real booking system).
export const DEPOSIT_TIMINGS = [
    { value: 'pay_to_hold', label: 'Pay to hold — collect before confirming the slot' },
    { value: 'book_then_pay', label: 'Book then pay — book first, send a payment link' },
];

// ---- intent <-> stored action mapping -------------------------------------
// A stored action is { id, label, order, is_enabled, handler_type, config }.
// The `icon` field is DROPPED by the revamp (widget doesn't require it).

// Handler types the intent model OWNS. Only these are re-serialised from the
// intent form on save; every other handler_type is a PASSTHROUGH (preserved
// verbatim, never rewritten to an intent). This prevents the MF4 regression
// where a legacy 'Call us' link / message / create_task / botpress_flow /
// payment action got collapsed into crm_form(enquiry) on a dirty save.
export const MANAGED_HANDLER_TYPES = ['crm_form', 'dentally', 'link'];
export const isManagedHandlerType = (ht) => MANAGED_HANDLER_TYPES.includes(ht);

// ---- link quick action: web address rules ----------------------------------
export const LINK_URL_MAX = 2000;
const HTTP_URL_RE = /^https?:\/\//i;

// True when `url` is an absolute http(s) address with a host (what the website chat opens).
export const isHttpUrl = (url) => {
    const v = `${url ?? ''}`.trim();
    if (!v || v.length > LINK_URL_MAX || !HTTP_URL_RE.test(v) || /\s/.test(v)) return false;
    try {
        const u = new URL(v);
        return (u.protocol === 'https:' || u.protocol === 'http:') && !!u.hostname;
    } catch (e) {
        return false;
    }
};

// Validate a link action's web address. `error` blocks saving; `warning` (http://) does not.
export const validateLinkUrl = (url) => {
    const v = `${url ?? ''}`.trim();
    if (!v) return { error: 'Enter the web address to open.', warning: null };
    if (v.length > LINK_URL_MAX) return { error: `Web address must be ${LINK_URL_MAX} characters or fewer.`, warning: null };
    if (!isHttpUrl(v)) return { error: 'Enter a full web address starting with https://', warning: null };
    if (/^http:\/\//i.test(v)) {
        return { error: null, warning: 'This address is not secure (http://). Use https:// if the site supports it.' };
    }
    return { error: null, warning: null };
};

// A stored 'link' action is editable only when its address is empty or http(s). A legacy link to
// tel:/mailto:/anything else stays a read-only passthrough so it round-trips untouched and can
// never block saving.
const isEditableLink = (a) => {
    const url = `${a?.config?.url ?? ''}`.trim();
    return !url || HTTP_URL_RE.test(url);
};
const isManagedAction = (a) => isManagedHandlerType(a?.handler_type)
    && (a.handler_type !== 'link' || isEditableLink(a));

// Derive the admin intent from a stored MANAGED action. Keyed off handler_type +
// config.operation + config.reason so existing saved configs map cleanly. Only
// called for managed handler types — passthrough actions never reach here.
export const intentOfAction = (a) => {
    const ht = a?.handler_type;
    const cfg = a?.config || {};
    if (ht === 'link') return 'link';
    if (ht === 'dentally') {
        const op = (cfg.operation || 'book').toLowerCase();
        if (op === 'reschedule') return 'reschedule';
        if (op === 'cancel') return 'cancel';
        // book / availability
        return cfg.reason === 'emergency' ? 'emergency' : 'book';
    }
    // crm_form → the enquiry funnel (the only non-dentally managed handler).
    return 'enquiry';
};

// Build the stored `config` for an intent, PRESERVING any keys the backend
// dispatcher or older configs rely on that this UI doesn't surface (fallback,
// practitioner_id, capture profile, etc.). `existing` is the current config.
// Talk-directly buttons (web config.contactButtons.style; link action config.contactCard.label).
export const CONTACT_BUTTON_STYLES = [
    { value: 'card', label: 'In the chat (card)' },
    { value: 'bar', label: 'Icon row beside the close button' },
    { value: 'icons', label: 'Round icons above the close button' },
];
export const CONTACT_CARD_LABEL_MAX = 30;
export const EMERGENCY_SAME_DAY_MAX = 200;
export const EMERGENCY_SAME_DAY_DEFAULT = 'We may be able to offer you a same-day appointment.';
export const CONTACT_CARD_DEFAULT_LABEL = 'Virtual Consult';

export const buildActionConfig = (intentValue, existing = {}) => {
    const intent = intentByValue[intentValue] || intentByValue.enquiry;
    const cfg = { ...existing };
    if (intent.handler_type === 'link') {
        // A link only needs its address: drop the funnel / dentally / task keys an earlier
        // intent may have left behind. Unknown keys (e.g. a legacy link_text) are kept.
        ['operation', 'reason', 'deposit', 'duration_minutes', 'appointment_reason', 'quick_contact',
            'confirmation_channel', 'confirmation_template_id', 'whatsapp_opt_in', 'ack', 'task',
            'lead_treatment_id'].forEach((k) => delete cfg[k]);
        if (typeof cfg.url === 'string') cfg.url = cfg.url.trim();
        else cfg.url = '';
        // "Also show in the talk-directly buttons": present = on; an empty label means the default.
        if (cfg.contactCard && typeof cfg.contactCard === 'object') {
            cfg.contactCard = { ...cfg.contactCard, label: `${cfg.contactCard.label ?? ''}`.trim().slice(0, CONTACT_CARD_LABEL_MAX) || CONTACT_CARD_DEFAULT_LABEL };
        } else delete cfg.contactCard;
    } else if (intent.handler_type === 'dentally') {
        delete cfg.contactCard;
        cfg.operation = intent.operation;
        if (intent.reason) cfg.reason = intent.reason;
        else delete cfg.reason;
    } else {
        // crm_form (enquiry): drop dentally-only keys so the config stays clean.
        delete cfg.contactCard;
        delete cfg.operation;
        delete cfg.reason;
        delete cfg.deposit;
        delete cfg.duration_minutes;
        delete cfg.appointment_reason;
        delete cfg.quick_contact;
    }
    return cfg;
};

// ---- per-action lead treatment + team task (contract 2026-10-12) ------------
// Stored on the managed action's config: `lead_treatment_id` (PracticeTreatment id; absent =
// Automatic) and `task` {enabled,title,priority,due_minutes}. Both are written ONLY when the admin
// edits them; buildActionConfig preserves them through its spread and never adds them.
export const TASK_PRIORITIES = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
];
export const TASK_DUE_PRESETS = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '1 h' },
    { value: 120, label: '2 h' },
    { value: 240, label: '4 h' },
    { value: 1440, label: '1 day' },
];
export const TASK_TITLE_MAX = 120;
export const TASK_DUE_MIN = 5;
export const TASK_DUE_MAX = 10080;

// Defaults the server applies when `config.task` is absent. due_minutes null = book's
// "within 1 working hour" rule (no due_minutes is written).
export const TASK_DEFAULTS = {
    emergency: { enabled: true, title: 'Emergency enquiry from chat', priority: 'high', due_minutes: 15 },
    book: { enabled: true, title: 'Booking request from chat', priority: 'normal', due_minutes: null },
    reschedule: { enabled: true, title: 'Reschedule request from chat', priority: 'high', due_minutes: 60 },
    cancel: { enabled: true, title: 'Cancel request from chat', priority: 'high', due_minutes: 60 },
    enquiry: { enabled: false, title: 'Enquiry from chat', priority: 'normal', due_minutes: 60 },
};

// The task as the editor shows it: the stored values over the per-intent defaults.
export const effectiveTask = (intentValue, cfg = {}) => {
    const d = TASK_DEFAULTS[intentValue] || TASK_DEFAULTS.enquiry;
    const t = cfg && typeof cfg.task === 'object' && cfg.task ? cfg.task : null;
    if (!t) return { ...d };
    return {
        enabled: intentValue === 'emergency' || (t.enabled === undefined ? d.enabled : !!t.enabled),
        title: typeof t.title === 'string' ? t.title : d.title,
        priority: TASK_PRIORITIES.some((p) => p.value === t.priority) ? t.priority : d.priority,
        due_minutes: Number.isInteger(t.due_minutes) ? t.due_minutes : d.due_minutes,
    };
};

// Clamp a minutes value into 5..10080 (integer); null when it is not a number.
export const clampDueMinutes = (v) => {
    const n = Math.round(Number(v));
    if (v === '' || v == null || !Number.isFinite(n)) return null;
    return Math.min(TASK_DUE_MAX, Math.max(TASK_DUE_MIN, n));
};

// Immutable config updaters: write the key only on an explicit edit; Automatic removes it.
export const withLeadTreatment = (cfg, id) => {
    const next = { ...(cfg || {}) };
    const n = Number(id);
    if (id === '' || id == null || !Number.isInteger(n)) delete next.lead_treatment_id;
    else next.lead_treatment_id = n;
    return next;
};
export const withTaskPatch = (intentValue, cfg, patch = {}) => {
    const merged = { ...effectiveTask(intentValue, cfg), ...patch };
    const task = {
        enabled: intentValue === 'emergency' ? true : !!merged.enabled,
        title: `${merged.title ?? ''}`.slice(0, TASK_TITLE_MAX),
        priority: merged.priority,
    };
    if (merged.due_minutes != null) task.due_minutes = clampDueMinutes(merged.due_minutes) ?? undefined;
    if (task.due_minutes === undefined) delete task.due_minutes;
    return { ...(cfg || {}), task };
};

// Map a full stored action → an editable form (for the UI).
//  - MANAGED (crm_form / dentally) → an intent form the editor can rewrite.
//  - PASSTHROUGH (anything else)   → the raw stored action kept VERBATIM under
//    `_raw`, flagged `_passthrough`, and rendered read-only. Only label,
//    is_enabled, order are editable; handler_type + config are never touched.
// Both keep id, order, is_enabled so reorder / enable / delete work uniformly.
export const actionToIntentForm = (a, index) => {
    if (!isManagedAction(a)) {
        // Preserve the ENTIRE stored action so it round-trips byte-for-byte
        // (any bespoke top-level keys, config, etc.). Editing only overrides
        // label / is_enabled / order at serialise time.
        return {
            id: a.id,
            _passthrough: true,
            handler_type: a.handler_type,
            label: a.label || '',
            is_enabled: a.is_enabled !== false,
            order: a.order ?? index,
            _raw: { ...a },
        };
    }
    const intentValue = intentOfAction(a);
    return {
        id: a.id,
        intent: intentValue,
        label: a.label || (intentByValue[intentValue]?.label ?? ''),
        is_enabled: a.is_enabled !== false,
        order: a.order ?? index,
        // Raw config is preserved verbatim so any keys the revamp doesn't surface
        // (fallback, practitioner_id, capture profile, etc.) round-trip on save.
        config: a.config || {},
    };
};

// Map an editable form → the stored action shape (for save).
export const intentFormToAction = (f, index) => {
    // PASSTHROUGH: re-emit the raw action UNCHANGED except for the three fields
    // the list lets the admin edit (label / is_enabled / order). handler_type
    // and config are copied straight from `_raw` — never rebuilt.
    if (f._passthrough) {
        return {
            ...(f._raw || {}),
            id: f.id,
            label: f.label,
            order: index,
            is_enabled: f.is_enabled !== false,
        };
    }
    const intent = intentByValue[f.intent] || intentByValue.enquiry;
    return {
        id: f.id,
        label: f.label,
        order: index,
        is_enabled: f.is_enabled !== false,
        handler_type: intent.handler_type,
        config: buildActionConfig(f.intent, f.config || {}),
    };
};

// ---- presets --------------------------------------------------------------
// Seed the 5 intents with sensible config for a practice's integration profile.
// Profiles: 'none' (no integrations), 'dentally', 'dentally_stripe'.
const genId = () => `a_${Math.random().toString(36).slice(2, 9)}`;

const baseAction = (intentValue, extraCfg = {}) => {
    const intent = intentByValue[intentValue];
    return {
        id: genId(),
        intent: intentValue,
        label: intent.label,
        is_enabled: true,
        order: 0,
        config: { confirmation_channel: 'in_chat', ...extraCfg },
    };
};

export const ACTION_PRESETS = {
    none: {
        label: 'No integrations (task + booking link)',
        build: () => ([
            baseAction('enquiry'),
            baseAction('book', { appointment_reason: 'New patient exam', booking_url_fallback: true }),
            baseAction('emergency', { appointment_reason: 'Emergency appointment', quick_contact: true }),
            baseAction('reschedule'),
            baseAction('cancel'),
        ]),
    },
    dentally: {
        label: 'Dentally (live booking)',
        build: () => ([
            baseAction('enquiry'),
            baseAction('book', { appointment_reason: 'New patient exam', duration_minutes: 30 }),
            baseAction('emergency', { appointment_reason: 'Emergency appointment', duration_minutes: 20, quick_contact: true }),
            baseAction('reschedule'),
            baseAction('cancel'),
        ]),
    },
    dentally_stripe: {
        label: 'Dentally + Stripe (deposit booking)',
        build: () => ([
            baseAction('enquiry'),
            baseAction('book', {
                appointment_reason: 'New patient exam', duration_minutes: 30,
                deposit: { enabled: true, amount_pounds: 50, timing: 'pay_to_hold' },
                confirmation_channel: 'auto',
            }),
            baseAction('emergency', { appointment_reason: 'Emergency appointment', duration_minutes: 20, quick_contact: true }),
            baseAction('reschedule'),
            baseAction('cancel'),
        ]),
    },
};

// Pick the preset key matching the practice's connected integrations, using the
// `available_types` map returned by the backend ({dentally:{available}, payment:{available}}).
export const presetKeyForProfile = (typeAvail = {}) => {
    const dentally = !!typeAvail?.dentally?.available;
    const stripe = !!typeAvail?.payment?.available;
    if (dentally && stripe) return 'dentally_stripe';
    if (dentally) return 'dentally';
    return 'none';
};

export const cssVariables = [
    ['main_bg_color', 'main-bg-color'],
    ['open_btn_bg', 'openButtonBground'],
    ['btn_text_color', 'buttonTextColor'],
    ['msg_icon_color', 'messageIcon'],
    ['single_shadow', 'single-shadow'],
    ['options_select_highlight', 'Options-select-hilight'],
    ['hover_text_color', 'hoverTextColor'],
    ['white_color_used', 'whiteColorUsed'],
    ['font_family', 'font-family'],
];

export const platformInitialValues = {
    platform: '',
    enabled: false,
    chatbot_platform_settings: {
        custom_css: {
            main_bg_color: '#fff',
            open_btn_bg: '#fff',
            btn_text_color: '#fff',
            msg_icon_color: '#fff',
            single_shadow: '#fff',
            options_select_highlight: '#fff',
            hover_text_color: '#fff',
            white_color_used: '#fff',
        }
    }
};

export const customizationFields = [
    {
        label: 'Show treatment-specific messages',
        fieldName: 'is_treatment_messages_enabled',
        description: 'After a visitor picks a treatment, the bot shows the messages and offers you configured for that treatment.',
    },
    {
        label: 'Ask for date of birth',
        fieldName: 'is_lead_dob_enabled',
        description: "Bot collects the visitor's date of birth during the enquiry.",
    },
    {
        label: 'Ask for postcode',
        fieldName: 'is_lead_postcode_enabled',
        description: "Bot collects the visitor's postcode during the enquiry.",
    },
    {
        label: 'Ask if existing patient',
        fieldName: 'is_existing_patient',
        description: 'Bot asks whether the visitor is already a patient at this practice.',
    },
    {
        label: 'Redirect existing patients to online booking link',
        fieldName: 'is_existing_patient_booking_redirect',
        description: "When a visitor says they're an existing patient, send them straight to the practice's online booking page instead of collecting their details. Requires 'Ask if existing patient' and an online booking URL on the practice.",
    },
    {
        label: 'Show booking link',
        fieldName: 'is_booking_url_enabled',
        description: "Bot shows a link to the practice's online booking page during the conversation.",
    },
    {
        label: 'Ask for preferred contact method',
        fieldName: 'is_preferred_contact_question_enabled',
        description: 'Bot asks how and when the visitor would like to be contacted (method, day, time).',
    },
    {
        label: 'Require privacy policy step',
        fieldName: 'is_privacy_policy_enabled',
        description: "Bot shows the practice's privacy-policy link in the welcome sequence. Turn on only if the practice requires it. Uses the practice's privacy policy URL.",
    },
];

// Plain-English reference used by the super admin chatbot console. Covers the
// flow toggles above plus config-level settings (Botpress bot, message speed).
export const messagePacingOptions = [
    { value: 'instant', label: 'Instant (no delay)', description: 'Messages appear immediately, with no artificial wait.' },
    { value: 'fast', label: 'Fast', description: 'Short ~150ms delay between messages.' },
    { value: 'normal', label: 'Normal', description: 'Default ~500ms delay between messages — natural, conversational pace.' },
];

export const chatbotFeatureGuide = [
    {
        label: 'Botpress bot',
        description: 'Which Botpress bot powers this chatbot’s conversation flow. Determines which flow/script version the practice runs (e.g. bot_071024). Edit it in the chatbot editor under Integrations.',
    },
    {
        label: 'Message speed',
        description: 'How fast the bot’s messages appear. Instant = no delay; Fast/Normal stagger them. A custom delay (ms) overrides the preset. Applies to the web chat and the Botpress flow.',
    },
    ...customizationFields.map(({ label, description }) => ({ label, description })),
];